import { DynamicFormConfig, DynamicFormSchema, FormDataType } from '@/components/DynamicForm/types/DynamicFormTypes';
import { OnSubmitCall, MappedSchemaType, SubmitHandler } from '@/components/DynamicForm/types/OnSubmitTypes';
import { buildSubmitPayload, formDataChangesFromSchema } from '@/components/DynamicForm/utils/dataMapper';
import { remapObjectWith } from '@/components/DynamicForm/utils/payloadParsing';
import { smplTmplt } from '@/components/DynamicForm';
import { BrandThemeConfig } from '@/types';
import { createCallBodyByContentType, redirectTo } from '@/components/DynamicForm/utils/utils';

const generateOnSubmitCall = async (schema: OnSubmitCall, formData: FormDataType) => {
	const { headers, method, url, payload } = schema;

	try {
		const headersToApply = {
			'Content-Type': 'application/json',
			...headers,
		};
		const builtPayload = buildSubmitPayload(formData, payload);
		const response = await fetch(url, {
			method,
			headers: headersToApply,
			...createCallBodyByContentType(headersToApply['Content-Type'], builtPayload),
		});
		const json: unknown = await response.json();
		return json;
	} catch (error) {
		return null;
	}
};

export const mapJsonSchema = (schema: DynamicFormSchema): MappedSchemaType => {
	return schema.reduce((prevState, stepInfo) => {
		const newFields = stepInfo.fields.reduce(
			(prevValue, curField) => ({
				...prevValue,
				[curField.name]: {
					label: curField.label,
					options: 'options' in curField ? curField.options : undefined,
					unique_label: 'unique_label' in curField ? curField.unique_label : undefined,
					name: curField.name,
				},
			}),
			{}
		);
		return { ...prevState, ...newFields };
	}, {});
};

export const handleSuccessCases = (successHandlers: SubmitHandler[], formDataToParse: Record<string, unknown>) => {
	let defaultHandler: SubmitHandler | undefined;
	let handler: SubmitHandler | undefined;

	successHandlers.forEach((successHandler) => {
		if (successHandler.condition === true || successHandler.condition === undefined) {
			defaultHandler = successHandler;
		} else {
			const { shouldTrigger } = remapObjectWith({ shouldTrigger: successHandler.condition }, formDataToParse);

			if (shouldTrigger) {
				handler = successHandler;
			}
		}
	});

	triggerHandler(handler || defaultHandler);
};

export const triggerHandler = (handler?: SubmitHandler) => {
	if (!handler) {
		return;
	}

	if (handler.action === 'redirect') {
		redirectTo(handler.url);
	}
};

export const onSubmit = async (
	schema: DynamicFormSchema,
	formData: FormDataType,
	config: {
		formConfig: DynamicFormConfig;
		appConfig: BrandThemeConfig;
	}
) => {
	const { formConfig, appConfig } = config;
	const formDataChanges = formDataChangesFromSchema(formConfig?.onSubmit?.deriveData ?? [], formData);
	const mappedSchema = mapJsonSchema(schema);

	const dataToParse = {
		...formDataChanges,
		config: {
			brand: appConfig.themeId || appConfig.name,
		},
		schema: mappedSchema,
	};

	const parsedFormConfig = JSON.parse(smplTmplt(JSON.stringify(formConfig), dataToParse)) as DynamicFormConfig;

	const onSubmitSchema = parsedFormConfig.onSubmit;

	for (const call of onSubmitSchema?.calls || []) {
		try {
			const remapFormData = remapObjectWith(call.payload, dataToParse);
			const response = await generateOnSubmitCall(call, remapFormData);

			if (call.handlers.success && response !== null) {
				handleSuccessCases(call.handlers.success, dataToParse);
			}
			// add handlers
		} catch (error) {
			console.error(error);
		}
	}
};
